import _ from 'underscore';

import { Messages } from '../../codegen/Localize';
import { imgPath } from '../../commonInfra/utils/ImgUrl';
import { AuthorizableType } from './AuthorizableType';

export enum Capabilities {
  addComment = 'add_comment',
  changeHierarchy = 'change_hierarchy',
  changePermissions = 'change_permissions',
  connect = 'connect',
  deleteItem = 'delete',
  editPublishedConnection = 'edit_published_connection',
  execute = 'execute',
  explainData = 'explain_data',
  exportData = 'export_data',
  exportImage = 'export_image',
  exportXml = 'export_xml',
  filter = 'filter',
  inheritedProjectLeader = 'inherited_project_leader', // This is a read-only capability, not shown in the permissions data grid
  projectLeader = 'project_leader',
  read = 'read',
  shareView = 'share_view',
  viewComments = 'view_comments',
  vizqlDataApiAccess = 'vizql_data_api_access',
  vud = 'vud',
  webAuthoring = 'web_authoring',
  extractRefresh = 'allow_extract_refresh',
  webAuthoringForFlows = 'web_authoring_for_flows',
  write = 'write',
  xmlSaveAs = 'xml_save_as',
  pulseMetricDefine = 'pulse_metric_define',
}

export interface ICapabilityInfo {
  getIcon: () => string;
  getDisabledIcon: () => string;
  message: () => string;
  name: string;
}

export const CapabilityInfo: _.Dictionary<ICapabilityInfo> = {
  addComment: {
    message: Messages.capability_add_comment,
    getIcon: () => imgPath('permission_capability_add_comment.svg'),
    getDisabledIcon: () => imgPath('permission_capability_add_comment_disabled.svg'),
    name: Capabilities.addComment
  },
  changeHierarchy: {
    message: Messages.capability_change_hierarchy,
    getIcon: () => imgPath('permission_capability_change_hierarchy.svg'),
    getDisabledIcon: () => imgPath('permission_capability_change_hierarchy_disabled.svg'),
    name: Capabilities.changeHierarchy
  },
  changePermissions: {
    message: Messages.capability_change_permissions,
    getIcon: () => imgPath('permission_capability_change_permissions.svg'),
    getDisabledIcon: () => imgPath('permission_capability_change_permissions_disabled.svg'),
    name: Capabilities.changePermissions
  },
  connect: {
    message: Messages.capability_connect,
    getIcon: () => imgPath('permission_capability_connect.svg'),
    getDisabledIcon: () => imgPath('permission_capability_connect_disabled.svg'),
    name: Capabilities.connect
  },
  delete: {
    message: Messages.capability_delete,
    getIcon: () => imgPath('permission_capability_delete.svg'),
    getDisabledIcon: () => imgPath('permission_capability_delete_disabled.svg'),
    name: Capabilities.deleteItem
  },
  editPublishedConnection: {
    message: Messages.capability_write_content,
    getIcon: () => imgPath('permission_capability_write.svg'),
    getDisabledIcon: () => imgPath('permission_capability_write_disabled.svg'),
    name: Capabilities.editPublishedConnection
  },
  editPredictiveModel: {
    message: Messages.capability_edit,
    getIcon: () => imgPath('permission_capability_edit_predictive_model.svg'),
    getDisabledIcon: () => imgPath('permission_capability_edit_predictive_model_disabled.svg'),
    name: Capabilities.webAuthoringForFlows
  },
  executeFlow: {
    message: Messages.capability_execute_flow,
    getIcon: () => imgPath('permission_capability_execute.svg'),
    getDisabledIcon: () => imgPath('permission_capability_execute_disabled.svg'),
    name: Capabilities.execute
  },
  explainData: {
    message: Messages.capability_explain_data,
    getIcon: () => imgPath('permission_capability_explain_data.svg'),
    getDisabledIcon: () => imgPath('permission_capability_explain_data_disabled.svg'),
    name: Capabilities.explainData
  },
  exportData: {
    message: Messages.capability_export_data,
    getIcon: () => imgPath('permission_capability_export_data.svg'),
    getDisabledIcon: () => imgPath('permission_capability_export_data_disabled.svg'),
    name: Capabilities.exportData
  },
  exportImage: {
    message: Messages.capability_export_image,
    getIcon: () => imgPath('permission_capability_export_image.svg'),
    getDisabledIcon: () => imgPath('permission_capability_export_image_disabled.svg'),
    name: Capabilities.exportImage
  },
  exportXmlDatasource: {
    message: Messages.capability_export_xml_datasource,
    getIcon: () => imgPath('permission_capability_export_xml_datasource.svg'),
    getDisabledIcon: () => imgPath('permission_capability_export_xml_datasource_disabled.svg'),
    name: Capabilities.exportXml
  },
  exportXmlFlow: {
    message: Messages.capability_export_xml_flow,
    getIcon: () => imgPath('permission_capability_export_xml_flow.svg'),
    getDisabledIcon: () => imgPath('permission_capability_export_xml_flow_disabled.svg'),
    name: Capabilities.exportXml
  },
  exportXmlWorkbook: {
    message: Messages.capability_export_xml_workbook_save_copy,
    getIcon: () => imgPath('permission_capability_export_xml_workbook.svg'),
    getDisabledIcon: () => imgPath('permission_capability_export_xml_workbook_disabled.svg'),
    name: Capabilities.exportXml
  },
  extractRefresh: {
    message: Messages.capability_allow_extract_refresh,
    getIcon: () => imgPath('permission_capability_extract_refresh.svg'),
    getDisabledIcon: () => imgPath('permission_capability_extract_refresh_disabled.svg'),
    name: Capabilities.extractRefresh
  },
  filter: {
    message: Messages.capability_filter,
    getIcon: () => imgPath('permission_capability_filter.svg'),
    getDisabledIcon: () => imgPath('permission_capability_filter_disabled.svg'),
    name: Capabilities.filter
  },
  projectLeader: {
    message: Messages.capability_project_leader,
    getIcon: () => imgPath('permission_capability_project_leader.svg'),
    getDisabledIcon: () => imgPath('permission_capability_project_leader_disabled.svg'),
    name: Capabilities.projectLeader
  },
  read: {
    message: Messages.capability_read,
    getIcon: () => imgPath('permission_capability_read.svg'),
    getDisabledIcon: () => imgPath('permission_capability_read_disabled.svg'),
    name: Capabilities.read
  },
  shareView: {
    message: Messages.capability_share_view,
    getIcon: () => imgPath('permission_capability_share_view.svg'),
    getDisabledIcon: () => imgPath('permission_capability_share_view_disabled.svg'),
    name: Capabilities.shareView
  },
  viewComments: {
    message: Messages.capability_view_comments,
    getIcon: () => imgPath('permission_capability_view_comments.svg'),
    getDisabledIcon: () => imgPath('permission_capability_view_comments_disabled.svg'),
    name: Capabilities.viewComments
  },
  vizqlDataApiAccess: {
    message: Messages.capability_vizql_data_api_access,
    getIcon: () => imgPath('permission_capability_vizql_data_api_access.svg'),
    getDisabledIcon: () => imgPath('permission_capability_vizql_data_api_access_disabled.svg'),
    name: Capabilities.vizqlDataApiAccess
  },
  vud: {
    message: Messages.capability_vud,
    getIcon: () => imgPath('permission_capability_vud.svg'),
    getDisabledIcon: () => imgPath('permission_capability_vud_disabled.svg'),
    name: Capabilities.vud
  },
  webAuthoring: {
    message: Messages.capability_web_authoring,
    getIcon: () => imgPath('permission_capability_web_authoring.svg'),
    getDisabledIcon: () => imgPath('permission_capability_web_authoring_disabled.svg'),
    name: Capabilities.webAuthoring
  },
  webAuthoringForFlows: {
    message: Messages.capability_web_authoring_for_flows,
    getIcon: () => imgPath('permission_capability_web_authoring_for_flows.svg'),
    getDisabledIcon: () => imgPath('permission_capability_web_authoring_for_flows_disabled.svg'),
    name: Capabilities.webAuthoringForFlows
  },
  write: {
    message: Messages.capability_write_content,
    getIcon: () => imgPath('permission_capability_write.svg'),
    getDisabledIcon: () => imgPath('permission_capability_write_disabled.svg'),
    name: Capabilities.write
  },
  writePredictiveModel: {
    message: Messages.capability_write_content,
    getIcon: () => imgPath('permission_capability_write.svg'),
    getDisabledIcon: () => imgPath('permission_capability_write_disabled.svg'),
    name: Capabilities.webAuthoringForFlows
  },
  writeProject: {
    message: Messages.capability_write_project,
    getIcon: () => imgPath('permission_capability_write.svg'),
    getDisabledIcon: () => imgPath('permission_capability_write_disabled.svg'),
    name: Capabilities.write
  },
  saveAsDatasource: {
    message: Messages.capability_xml_save_as,
    getIcon: () => imgPath('permission_capability_save_as_datasource.svg'),
    getDisabledIcon: () => imgPath('permission_capability_save_as_datasource_disabled.svg'),
    name: Capabilities.xmlSaveAs
  },
  definePulseMetric: {
    message: Messages.capability_pulse_metric_define,
    getIcon: () => imgPath('permission_capability_define_pulse_metric.svg'),
    getDisabledIcon: () => imgPath('permission_capability_define_pulse_metric_disabled.svg'),
    name: Capabilities.pulseMetricDefine
  },
};

const CollectionCapabilities = [
  Capabilities.read
];

const CatalogCapabilities = [
  Capabilities.changeHierarchy,
  Capabilities.changePermissions,
  Capabilities.read,
  Capabilities.write
];

const DataRoleCapabilities = [
  Capabilities.changeHierarchy,
  Capabilities.changePermissions,
  Capabilities.deleteItem,
  Capabilities.read,
  Capabilities.write
];

const LensCapabilities = [
  Capabilities.changeHierarchy,
  Capabilities.changePermissions,
  Capabilities.deleteItem,
  Capabilities.read,
  Capabilities.write
];

const DatasourceCapabilities = (vizqlDataServicePermissionEnabled?: boolean, ntbueCheckMetricDefineCap?: boolean, allowNonAdminRefreshExtracts?: boolean) => [
  Capabilities.changeHierarchy,
  Capabilities.changePermissions,
  Capabilities.connect,
  Capabilities.deleteItem,
  Capabilities.exportXml,
  Capabilities.read,
  Capabilities.write,
  Capabilities.xmlSaveAs,
  ...ntbueCheckMetricDefineCap ? [Capabilities.pulseMetricDefine] : [],
  ...allowNonAdminRefreshExtracts ? [Capabilities.extractRefresh] : [],
  ...vizqlDataServicePermissionEnabled ? [Capabilities.vizqlDataApiAccess] : []
];

const FlowCapabilities = [
  Capabilities.changeHierarchy,
  Capabilities.changePermissions,
  Capabilities.deleteItem,
  Capabilities.execute,
  Capabilities.exportXml,
  Capabilities.read,
  Capabilities.webAuthoringForFlows,
  Capabilities.write
];

const PredictiveModelCapabilities = [
  Capabilities.read,
  Capabilities.changeHierarchy,
  Capabilities.webAuthoringForFlows,
  Capabilities.deleteItem,
  Capabilities.changePermissions,
];

const PublishedConnectionCapabilities = [
  Capabilities.changeHierarchy,
  Capabilities.changePermissions,
  Capabilities.connect,
  Capabilities.deleteItem,
  Capabilities.editPublishedConnection,
  Capabilities.read,
];

const ProjectCapabilities = [
  Capabilities.read,
  Capabilities.write
];

const ViewCapabilities = [
  Capabilities.addComment,
  Capabilities.changePermissions,
  Capabilities.deleteItem,
  Capabilities.exportData,
  Capabilities.exportImage,
  Capabilities.filter,
  Capabilities.read,
  Capabilities.shareView,
  Capabilities.viewComments,
  Capabilities.vud,
  Capabilities.webAuthoring
];

const WorkbookCapabilities = (allowNonAdminRefreshExtracts?: boolean) => [
  Capabilities.addComment,
  Capabilities.changeHierarchy,
  Capabilities.changePermissions,
  Capabilities.deleteItem,
  Capabilities.explainData,
  Capabilities.exportData,
  Capabilities.exportImage,
  Capabilities.exportXml,
  Capabilities.filter,
  Capabilities.read,
  Capabilities.shareView,
  Capabilities.viewComments,
  Capabilities.vud,
  ...allowNonAdminRefreshExtracts ? [Capabilities.extractRefresh] : [],
  Capabilities.webAuthoring,
  Capabilities.write
];

export const CapabilitiesByAuthorizableType = (vizqlDataServicePermissionEnabled?: boolean, ntbueCheckMetricDefineCap?: boolean, allowNonAdminRefreshExtracts?: boolean): _.Dictionary<Capabilities[]> => {
  return {
    [AuthorizableType.COLLECTION]: CollectionCapabilities,
    [AuthorizableType.DATABASE]: CatalogCapabilities,
    [AuthorizableType.DATABASE_TABLE_TEMPLATE]: CatalogCapabilities,
    [AuthorizableType.DATAROLE]: DataRoleCapabilities,
    [AuthorizableType.DATASOURCE]: DatasourceCapabilities(vizqlDataServicePermissionEnabled, ntbueCheckMetricDefineCap, allowNonAdminRefreshExtracts),
    [AuthorizableType.FLOW]: FlowCapabilities,
    [AuthorizableType.LENS]: LensCapabilities,
    [AuthorizableType.PREDICTIVE_MODEL]: PredictiveModelCapabilities,
    [AuthorizableType.PUBLISHED_CONNECTION]: PublishedConnectionCapabilities,
    [AuthorizableType.PROJECT]: ProjectCapabilities,
    [AuthorizableType.PROJECT_DATABASE_TEMPLATE]: CatalogCapabilities,
    [AuthorizableType.PROJECT_DATASOURCE_TEMPLATE]: DatasourceCapabilities(vizqlDataServicePermissionEnabled, ntbueCheckMetricDefineCap, allowNonAdminRefreshExtracts),
    [AuthorizableType.PROJECT_DATA_ROLE_TEMPLATE]: DataRoleCapabilities,
    [AuthorizableType.PROJECT_FLOW_TEMPLATE]: FlowCapabilities,
    [AuthorizableType.PROJECT_LENS_TEMPLATE]: LensCapabilities,
    [AuthorizableType.PROJECT_PREDICTIVE_MODEL_TEMPLATE]: PredictiveModelCapabilities,
    [AuthorizableType.PROJECT_PUBLISHED_CONNECTION_TEMPLATE]: PublishedConnectionCapabilities,
    [AuthorizableType.PROJECT_TABLE_TEMPLATE]: CatalogCapabilities,
    [AuthorizableType.PROJECT_WORKBOOK_TEMPLATE]: WorkbookCapabilities(allowNonAdminRefreshExtracts),
    [AuthorizableType.TABLE]: CatalogCapabilities,
    [AuthorizableType.VIEW]: ViewCapabilities,
    [AuthorizableType.WORKBOOK]: WorkbookCapabilities(allowNonAdminRefreshExtracts),
  };
};
